/* import __COLOCATED_TEMPLATE__ from './trial-badge.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinTrialService from 'embercom/services/fin-trial-service';
import type IntlService from 'ember-intl/services/intl';
import { AiAnswersState, selectedAgentToTrialType } from 'embercom/lib/operator/fin/types';
import moment from 'moment-timezone';

interface Arguments {
  canUseCustomAnswers?: boolean;
  selectedAgentValue?: string;
}

interface Signature {
  Args: Arguments;
  Element: HTMLDivElement;
}

export default class TrialBadge extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finTrialService: FinTrialService;

  private get usePerAgentTrials(): boolean {
    return !!this.args.selectedAgentValue;
  }

  private get agentTrialType() {
    return selectedAgentToTrialType(this.args.selectedAgentValue ?? 'fin');
  }

  get showStamp() {
    if (this.usePerAgentTrials) {
      return (
        this.finTrialService.showAgentCTAs(this.agentTrialType) && !this.args.canUseCustomAnswers
      );
    }
    return this.finTrialService.showCTAs && !this.args.canUseCustomAnswers;
  }

  get stampText() {
    if (!this.showStamp) {
      return '';
    }

    if (this.usePerAgentTrials) {
      let agentState = this.finTrialService.getAgentTrialState(this.agentTrialType);
      if (agentState?.state === AiAnswersState.trialStarted && agentState.trialDetails?.endDate) {
        let remainingDays = moment(agentState.trialDetails.endDate).diff(moment(), 'days');
        if (remainingDays <= 0) {
          return this.intl.t('operator.fin.free-trial-copy.trial-badge.trial-ends-today');
        }
        return this.intl.t('operator.fin.free-trial-copy.trial-badge.trial-ends-in-days', {
          remainingDays,
        });
      }
      return this.intl.t('operator.fin.free-trial-copy.trial-badge.free-for-14-days');
    }

    if (this.finTrialService.freeTrialStarted) {
      let remainingDays = this.finTrialService.trialDaysRemaining;
      if (remainingDays <= 0) {
        return this.intl.t('operator.fin.free-trial-copy.trial-badge.free-usage-ends-today');
      }
      return this.intl.t('operator.fin.free-trial-copy.trial-badge.days-free-remaining', {
        remainingDays,
      });
    }
    return this.intl.t('operator.fin.free-trial-copy.trial-badge.free-for-14-days');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::TrialBadge': typeof TrialBadge;
  }
}
